/**
 * @file ColorsTab.tsx
 * @description Вкладка «Цвета» оформления сайта (phase19.16 Р1; SB8, SB9;
 * uiux-design-r1 §1): девять ролей группами «Фон и поверхности», «Текст»,
 * «Действия», «Тёмные секции»; окно цвета по нажатию на строку.
 * Производные цвета (наведение, линии, фокус, ошибки) считает платформа.
 *
 * SB122 (uiux-settings-groups §5 шаг 3): группы — карточки `SettingsGroup`
 * по реестру `SITE_COLOR_GROUP_LABELS`, панель `colors` (память
 * `commora.settings-groups.colors`); у свёрнутой — образцы цветов и сводка
 * «по умолчанию / свои», «!» — если контраст какой-то роли ниже нормы.
 */

import { SITE_COLOR_GROUP_LABELS, type SiteColorRole, type SiteColors } from "@chaiberi/shared";
import { useState } from "react";

import { SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { ColorPickerDialog } from "./ColorPickerDialog";
import { ColorRoleRow, Swatch } from "./ColorRoleRow";
import { CONTRAST_EXPLAINED } from "./ContrastBadge";
import { COLOR_GROUP_TOC, colorGroupView, SITE_COLOR_GROUPS } from "../../../lib/site-view/color-groups";

interface Props {
  colors: SiteColors;
  onChange: (role: SiteColorRole, hex: string) => void;
}

export function ColorsTab({ colors, onChange }: Props) {
  const [editing, setEditing] = useState<SiteColorRole | null>(null);

  return (
    <SettingsGroups panel="colors">
      {/* «Аа 4,52 ✓» у цвета — контраст; объяснение строкой (на касании подсказки не видно). */}
      <p className="type-caption text-on-surface-variant">{CONTRAST_EXPLAINED}</p>
      {SITE_COLOR_GROUPS.map((group) => {
        const view = colorGroupView(group, colors);
        return (
          <SettingsGroup
            key={group}
            id={group}
            title={SITE_COLOR_GROUP_LABELS[group]}
            tocLabel={COLOR_GROUP_TOC[group]}
            status={view.warning ? "warning" : undefined}
            summary={
              <span className="inline-flex flex-wrap items-center gap-1.5">
                <span aria-hidden className="inline-flex gap-0.5">
                  {view.roles.map((role) => (
                    <Swatch key={role} color={colors[role]} size={14} />
                  ))}
                </span>
                {view.summary}
              </span>
            }
          >
            {/* Строки без своей рамки: карточка группы уже рамка (не «карточка в карточке»). */}
            <div className="-mx-2 flex flex-col divide-y divide-[var(--border)]">
              {view.roles.map((role) => (
                <ColorRoleRow key={role} role={role} colors={colors} onOpen={() => setEditing(role)} onApply={(hex) => onChange(role, hex)} />
              ))}
            </div>
          </SettingsGroup>
        );
      })}
      <p className="type-caption text-on-surface-variant">Наведение, линии, фокус и цвета ошибок платформа считает сама.</p>
      <ColorPickerDialog
        role={editing}
        colors={colors}
        onClose={() => setEditing(null)}
        onDone={(role, hex) => {
          onChange(role, hex);
          setEditing(null);
        }}
      />
    </SettingsGroups>
  );
}
